import React from 'react';
import { cn } from '@/lib/utils';
import { SPACING, DIMENSIONS, TYPOGRAPHY, RADIUS } from '@/lib/design-tokens';

/**
 * PageContainer enforces consistent layout spacing anchored to the sidebar.
 * All admin pages MUST use this container to maintain structural alignment.
 * 
 * Layout Constants (locked to sidebar):
 * - Horizontal padding: 24px (SPACING.PAGE_HORIZONTAL)
 * - Max content width: 1400px (DIMENSIONS.CONTENT_XL)
 * - Vertical padding: 32px top + 24px bottom (SPACING.PAGE_VERTICAL)
 * - All page titles, breadcrumbs, and actions align on same vertical axis
 * 
 * DO NOT override these spacing values without architectural justification.
 */

interface PageContainerProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Page header content (title, description, actions)
   * Will be aligned consistently across all pages
   */
  header?: React.ReactNode;
  /**
   * Maximum width constraint
   * @default "xl" (1400px)
   */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
}

const maxWidthClasses = {
  'sm': DIMENSIONS.CONTENT_SM,
  'md': DIMENSIONS.CONTENT_MD,
  'lg': DIMENSIONS.CONTENT_LG,
  'xl': DIMENSIONS.CONTENT_XL,
  'full': 'max-w-full',
};

export const PageContainer: React.FC<PageContainerProps> = ({
  children,
  className,
  header,
  maxWidth = 'xl',
}) => {
  return (
    <div className="min-h-screen bg-background">
      <div className={cn(
        'mx-auto',
        SPACING.PAGE_HORIZONTAL,
        SPACING.PAGE_VERTICAL,
        maxWidthClasses[maxWidth],
        className
      )}>
        {header && (
          <div className="mb-8">
            {header}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

/**
 * PageHeader component for consistent page title/action alignment
 * Use this for all page headers to maintain vertical axis alignment
 */
interface PageHeaderProps {
  icon?: React.ComponentType<{ className?: string }>;
  title: string;
  description?: string;
  actions?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon: Icon,
  title,
  description,
  actions,
}) => {
  return (
    <div className="flex items-start justify-between">
      <div className={cn('flex items-center', SPACING.ELEMENT_GAP)}>
        {Icon && (
          <div className={cn('p-2 bg-primary/10', RADIUS.LG)}>
            <Icon className={DIMENSIONS.ICON_LG} />
          </div>
        )}
        <div>
          <h1 className={cn(TYPOGRAPHY.PAGE_TITLE, 'tracking-tight')}>
            {title}
          </h1>
          {description && (
            <p className={cn(TYPOGRAPHY.PAGE_DESCRIPTION, 'mt-1')}>
              {description}
            </p>
          )}
        </div>
      </div>
      {actions && (
        <div className={cn('flex items-center', SPACING.TIGHT_GAP)}>
          {actions}
        </div>
      )}
    </div>
  );
};

/**
 * ContentSection component for consistent card/section spacing
 * All content sections snap to this structure
 */
interface ContentSectionProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Spacing between items
   * @default "card" (24px gap between cards)
   */
  spacing?: 'tight' | 'card' | 'section';
}

const spacingClasses = {
  'tight': 'space-y-4',      // 16px
  'card': 'space-y-6',       // 24px - Default
  'section': 'space-y-8',    // 32px
};

export const ContentSection: React.FC<ContentSectionProps> = ({
  children,
  className,
  spacing = 'card',
}) => {
  return (
    <div className={cn(spacingClasses[spacing], className)}>
      {children}
    </div>
  );
};
